//components/shared/PurchaseConfirmModal.tsx

"use client";

import { useEffect, useState } from "react";
import { X, Sparkles, Gem, Plus, Ticket, Loader2 } from "lucide-react";
import { previewCoupon } from "@/actions/coupons";
import { normalizeCouponCode } from "@/lib/coupon";
import { formatDiamond } from "@/utils/formatDiamond";
import TopupQrModal from "@/components/wallet/TopupQrModal";
import Button from "@/components/shared/Button";
import UnlockButton from "@/components/shared/UnlockButton";
import { INTERVIEW_QUESTIONS_PREVIEW } from "@/lib/interview/types";

interface Props {
  open: boolean;
  type: "exam" | "path" | "interview";
  title: string;
  price: number;
  hasDemo: boolean;       // true nếu có ≥1 câu demo (demo_limit > 0 với exam, có path_items.is_demo với path)
  userBalance: number;    // số dư hiện tại của user
  onConfirm: (couponCode?: string) => void;  // khi user click "Mua" (kèm mã đã áp dụng nếu có)
  itemId?: string;        // có + type path/exam → hiện ô mã giảm giá
  initialCoupon?: string; // mã từ URL ?coupon= → tự áp dụng khi mở
  onViewDemo: () => void; // khi user click "Xem demo"
  onClose: () => void;
}

export default function PurchaseConfirmModal({
  open, type, title, price, hasDemo, userBalance,
  onConfirm, onViewDemo, onClose, itemId, initialCoupon,
}: Props) {
  const [showTopup, setShowTopup] = useState(false);

  // ── Mã giảm giá: giá sau giảm do server trả, client chỉ hiển thị ──
  const couponEnabled = !!itemId && (type === "path" || type === "exam");
  const [couponInput, setCouponInput] = useState("");
  const [applied, setApplied] = useState<{ code: string; discount: number; final: number } | null>(null);
  const [couponErr, setCouponErr] = useState("");
  const [checking, setChecking] = useState(false);

  async function applyCoupon(raw: string) {
    if (!couponEnabled || !itemId) return;
    const code = normalizeCouponCode(raw);
    if (!code) { setApplied(null); setCouponErr("Mã không hợp lệ"); return; }
    setChecking(true);
    setCouponErr("");
    try {
      const r = await previewCoupon(type as "path" | "exam", itemId, code);
      if (r.ok) setApplied({ code: r.code, discount: r.discount, final: r.final });
      else { setApplied(null); setCouponErr(r.error); }
    } catch {
      setCouponErr("Không kiểm tra được mã, vui lòng thử lại");
    } finally {
      setChecking(false);
    }
  }

  useEffect(() => {
    if (!open) { setApplied(null); setCouponErr(""); return; }
    if (initialCoupon && couponEnabled) {
      setCouponInput(initialCoupon);
      applyCoupon(initialCoupon);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, initialCoupon]);

  // ESC để đóng
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  const finalPrice   = applied?.final ?? price;
  const insufficient = userBalance < finalPrice;
  const typeLabel =
    type === "exam" ? "đề thi"
    : type === "path" ? "lộ trình"
    : "phỏng vấn";

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm animate-fade-in"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden animate-scale-in"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="relative bg-gradient-to-br from-teal-700 to-teal-900 px-6 py-5">
          <button
            onClick={onClose}
            className="absolute top-3 right-3 w-8 h-8 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white transition"
            aria-label="Đóng"
          >
            <X className="w-4 h-4" />
          </button>
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-2xl bg-amber-400 flex items-center justify-center">
              <Gem className="w-6 h-6 text-white" />
            </div>
            <div>
              <p className="text-[10px] text-white/60 font-bold uppercase tracking-wider">Mở khóa {typeLabel}</p>
              <h3 className="text-white text-[16px] font-extrabold leading-tight line-clamp-2">{title}</h3>
            </div>
          </div>
        </div>

        {/* Body */}
        <div className="px-6 py-5">
          <div className="flex items-baseline justify-between mb-1">
            <span className="text-[12px] text-muted-foreground">Giá</span>
            <div className="flex items-baseline gap-1.5">
              {applied && (
                <span className="text-[13px] text-muted-foreground line-through tabular-nums">{formatDiamond(price)}</span>
              )}
              <span className="text-[24px] font-extrabold text-teal-800 tabular-nums">{formatDiamond(finalPrice)}</span>
              <span className="text-[16px]">💎</span>
            </div>
          </div>
          <div className="flex items-center justify-between text-[11px] text-muted-foreground mb-4">
            <span>Số dư của bạn</span>
            <span className={`font-bold tabular-nums ${insufficient ? "text-red-500" : "text-emerald-600"}`}>
              {formatDiamond(userBalance)} 💎
            </span>
          </div>

          {couponEnabled && (
            <div className="mb-4">
              <div className="flex gap-2">
                <div className="relative flex-1">
                  <Ticket className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" aria-hidden />
                  <input
                    value={couponInput}
                    onChange={e => { setCouponInput(e.target.value.toUpperCase()); setCouponErr(""); if (applied) setApplied(null); }}
                    onKeyDown={e => { if (e.key === "Enter") applyCoupon(couponInput); }}
                    placeholder="Mã giảm giá"
                    aria-label="Mã giảm giá"
                    maxLength={32}
                    autoComplete="off"
                    className="w-full h-9 pl-8 pr-2 rounded-lg border border-border text-[12px] font-semibold tracking-wide uppercase focus:outline-none focus:border-teal-600"
                  />
                </div>
                <button
                  type="button"
                  onClick={() => applyCoupon(couponInput)}
                  disabled={checking || !couponInput.trim()}
                  className="h-9 px-3 rounded-lg bg-teal-700 text-white text-[12px] font-bold hover:bg-teal-600 disabled:opacity-50 transition-colors inline-flex items-center gap-1"
                >
                  {checking ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : "Áp dụng"}
                </button>
              </div>
              {applied && (
                <p className="mt-1.5 text-[11px] font-semibold text-emerald-600">
                  ✓ Đã áp dụng mã {applied.code}: giảm {formatDiamond(applied.discount)} 💎
                </p>
              )}
              {couponErr && <p className="mt-1.5 text-[11px] font-semibold text-red-600" role="alert">{couponErr}</p>}
            </div>
          )}

          {insufficient && (
            <div className="mb-4 px-3 py-2 rounded-lg bg-red-50 border border-red-200 flex items-center gap-2">
              <span className="flex-1 text-[11px] text-red-700 font-semibold">
                Bạn không đủ kim cương. Vui lòng nạp thêm.
              </span>
              <button
                onClick={() => setShowTopup(true)}
                className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-orange-500 text-white text-[10px] font-bold hover:bg-orange-400 transition-colors shrink-0"
              >
                <Plus className="w-3 h-3" />Nạp thêm
              </button>
            </div>
          )}

          {hasDemo && (
            <div className="mb-4 px-3 py-2.5 rounded-lg bg-amber-50 border border-amber-200 flex items-start gap-2">
              <Sparkles className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
              <p className="text-[11px] text-amber-700 leading-relaxed">
                {type === "exam"
                  ? `Đề này có ${INTERVIEW_QUESTIONS_PREVIEW} câu demo miễn phí`
                  : type === "path"
                  ? "Lộ trình này có câu demo miễn phí"
                  : "Có nội dung xem trước miễn phí"}. Bạn có thể xem trước trước khi quyết định mua.
              </p>
            </div>
          )}

          {/* Buttons */}
          <div className="flex gap-2.5">
            {hasDemo && (
              <Button
                variant="arcade"
                tone="slate"
                onClick={() => { onViewDemo(); onClose(); }}
                className="flex-1 !bg-white !text-teal-700 border-2 border-teal-700 hover:!bg-teal-50 !h-auto py-3 !rounded-xl !text-[13px]"
              >
                Xem demo
              </Button>
            )}
            {insufficient ? (
              <button
                onClick={() => setShowTopup(true)}
                className="flex-1 py-3 rounded-xl bg-orange-500 text-white text-[13px] font-bold hover:bg-orange-600 transition-colors shadow-[0_2px_8px_rgba(249,115,22,.3)]"
              >
                Nạp {formatDiamond(finalPrice - userBalance)} 💎 &amp; mua ngay
              </button>
            ) : (
              <UnlockButton onClick={() => onConfirm(applied?.code)} tone="orange" awaitAnimation className="flex-1">
                {hasDemo ? "Mua ngay" : `Mua với ${formatDiamond(finalPrice)} 💎`}
              </UnlockButton>
            )}
          </div>
        </div>
      </div>

      {showTopup && (
        <TopupQrModal
          need={finalPrice - userBalance}
          onClose={() => setShowTopup(false)}
          onPaid={() => { setShowTopup(false); onConfirm(applied?.code); }}  // nạp xong → mua luôn, giữ mã
        />
      )}
    </div>
  );
}